// ✅ Add these lazy imports at the top
import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { TrackedLayout } from '../component/TrackedLayout';
import PublicLayout from '../component/PublicLayout';
import ProtectedRoute from '../routes/ProtectedRoute';
import { ScrollToTop } from '../component/ScrollToTop';

// ✅ fix — lazy load every page
const Home = lazy(() => import('../pages/Home'));
const Login = lazy(() => import('../component/Login'));
const Category = lazy(() => import('../pages/Category'));
const BookReviews = lazy(() => import('../pages/BookReviews'));
const Review = lazy(() => import('../pages/Review'));
const Author = lazy(() => import('../pages/Author/Author'));
const User = lazy(() => import('../pages/UserInfo/User'));
const BookDetails = lazy(() => import('../pages/BookDetails'));
const BookFairs = lazy(() => import('../pages/Events/BookFair/BookFairs'));
const BookFair = lazy(() => import('../pages/Events/BookFair/BookFair'));
const BookPreReleases = lazy(() => import('../pages/Events/BookPreRelease/BookPreReleases'));
const BookPreRelease = lazy(() => import('../pages/Events/BookPreRelease/BookPreRelease'));
const BookLaunches = lazy(() => import('../pages/Events/BookLaunch/BookLaunches'));
const BookLaunch = lazy(() => import('../pages/Events/BookLaunch/BookLaunch'));
const BookOffers = lazy(() => import('../pages/Events/BookOffer/BookOffers'));
const BookOffer = lazy(() => import('../pages/Events/BookOffer/BookOffer'));
const Awards = lazy(() => import('../pages/Events/Award/Awards'));
const Award = lazy(() => import('../pages/Events/Award/Award'));
const BookEvents = lazy(() => import('../pages/Events/BookEvent/BookEvents'));
const BookEvent = lazy(() => import('../pages/Events/BookEvent/BookEvent'));
const AuthorSeries = lazy(() => import('../pages/Author/AuthorSeries'));
const Feedback = lazy(() => import('../pages/ContactUs/Feedback'));
const ContactUs = lazy(() => import('../pages/ContactUs/ContactUs'));
const AboutUs = lazy(() => import('../pages/ContactUs/AboutUs'));
const MyReviews = lazy(() => import('../pages/UserActivity/MyReviews'));
const AuthorReview = lazy(() => import('../pages/BookReview/AuthorReview'));
const UserReview = lazy(() => import('../pages/BookReview/UserReview'));
const ChangePassword = lazy(() => import('../auth/ChangePassword'));
const ForgotPassword = lazy(() => import('../auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../auth/ResetPassword'));
const AccountLocked = lazy(() => import('../pages/Error/AccountLocked'));
const ContactSupport = lazy(() => import('../pages/Error/ContactSupport'));
const UserProfile = lazy(() => import('../pages/UserInfo/UserProfile'));
const MyProfile = lazy(() => import('../pages/UserInfo/MyProfile'));
const AddReview = lazy(() => import('../pages/Review/AddReview'));
const MyLibrary = lazy(() => import('../pages/UserInfo/MyLibrary'));
const SearchResults = lazy(() => import('../component/Search/SearchResults'));

// ✅ Better loading fallback component
function PageLoader() {
  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        minHeight: '60vh',
        backgroundColor: 'var(--bs-body-bg, #fff)'
      }}
    >
      <div className="spinner-border text-danger" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
}

function AllRoutes() {
  return (
    <>
      <ScrollToTop />
      {/* ✅ Single Suspense wraps all routes */}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/ForgotPassword" element={<ForgotPassword />} />
            <Route path="/ResetPassword" element={<ResetPassword />} /> 
          </Route>

          <Route element={<TrackedLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/Category/:parentId/:categoryName" element={<Category />} />
            <Route path="/bookreviews/:id/:slug" element={<BookReviews />} />
            <Route path="/review/:id/:title" element={<Review />} />
            <Route path="/Author" element={<Author />} />
            <Route path="/user/:id/:userName" element={<User />} />
            <Route path="/bookdetails/:id/:title" element={<BookDetails />} />
            <Route path="/bookFairs" element={<BookFairs />} />
            <Route path="/bookfair/:id/:title" element={<BookFair />} />
            <Route path="/bookPreReleases" element={<BookPreReleases />} />
            <Route path="/bookprerelease/:id/:title" element={<BookPreRelease />} />
            <Route path="/bookLaunches" element={<BookLaunches />} />
            <Route path="/bookLaunch/:id/:title" element={<BookLaunch />} />
            <Route path="/bookOffers" element={<BookOffers />} />
            <Route path="/bookOffer/:id/:title" element={<BookOffer />} />
            <Route path="/awards" element={<Awards />} />
            <Route path="/award/:id/:title" element={<Award />} />
            <Route path="/bookEvents" element={<BookEvents />} />
            <Route path="/bookEvent/:id/:title" element={<BookEvent />} />
            <Route path="/authorSeries" element={<AuthorSeries />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/aboutUs" element={<AboutUs />} />
            <Route path="/contactUs" element={<ContactUs />} />
            <Route path="/myReviews" element={<MyReviews />} />
            <Route path="/authorReview" element={<AuthorReview />} />
            <Route path="/userReview" element={<UserReview />} />
            <Route path="/account-locked" element={<AccountLocked />} />
            <Route path="/contact-support" element={<ContactSupport />} />

            <Route path="/UserProfile" element={
              <ProtectedRoute><UserProfile /></ProtectedRoute>
            } />
            <Route path="/MyProfile" element={
              <ProtectedRoute><MyProfile /></ProtectedRoute>
            } />
            <Route path="/AddReview/:bookDetailsId/:bookName" element={
              <ProtectedRoute><AddReview /></ProtectedRoute>
            } />
            <Route path="/ChangePassword" element={
              <ProtectedRoute><ChangePassword /></ProtectedRoute>
            } />
            <Route path="/myLibrary" element={
              <ProtectedRoute><MyLibrary /></ProtectedRoute>
            } />
            {/* <Route element={<ProtectedRoute isLoggedIn={isLoggedIn} />}>
              <Route path="/myLibrary" element={<MyLibrary userId={id} />} />
            </Route> */}

          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

export default AllRoutes;